import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Opportunity } from "@entities";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import {
  TIME_DESCRIPTION_MAX_LENGTH,
  WEEK_DAYS,
} from "@shared/helpers/constants";
import { hasValidCoordinates } from "@shared/helpers/geo";
import {
  buildBulkGroupChildPreview,
  buildBulkGroupChildSlots,
  buildBulkMultiLocationPreview,
  buildBulkMultiLocationSlots,
  countSchedulePreview,
  GROUP_INSTANCE_BULK_MAX,
  GROUP_INSTANCE_BULK_WARN_THRESHOLD,
  type BulkLocationEntry,
  type BulkMultiLocationSchedule,
  type GroupInstanceScheduleRangeMode,
  type GroupInstanceSlotIdentity,
  type GroupInstanceSlotPreview,
  type MultiLocationSlotPreview,
  validateBulkMultiLocationConfig,
  validateGroupInstanceScheduleConfig,
} from "@shared/helpers/group-instance-schedule";
import {
  ADMIN_GROUP_BULK_ADD_LOCATION,
  ADMIN_GROUP_BULK_CREATE,
  ADMIN_GROUP_BULK_DAYS_LABEL,
  ADMIN_GROUP_BULK_END_DATE,
  ADMIN_GROUP_BULK_ERROR,
  ADMIN_GROUP_BULK_GENERATE_HINT,
  ADMIN_GROUP_BULK_GENERATE_TITLE,
  ADMIN_GROUP_BULK_LOCATION_ROW,
  ADMIN_GROUP_BULK_LOCATIONS_HINT,
  ADMIN_GROUP_BULK_MODE_BY_DAYS,
  ADMIN_GROUP_BULK_MODE_BY_LOCATIONS,
  ADMIN_GROUP_BULK_PREVIEW,
  ADMIN_GROUP_BULK_PREVIEW_SUMMARY,
  ADMIN_GROUP_BULK_RANGE_UNTIL_DATE,
  ADMIN_GROUP_BULK_RANGE_WEEKS,
  ADMIN_GROUP_BULK_START_DATE,
  ADMIN_GROUP_BULK_STATUS_NEW,
  ADMIN_GROUP_BULK_STATUS_PAST,
  ADMIN_GROUP_BULK_STATUS_SKIP,
  ADMIN_GROUP_BULK_WARN_MANY,
  ADMIN_GROUP_BULK_WEEK_COUNT,
} from "@shared/helpers/opportunity-group-admin-copy";
import dayjs, { type Dayjs } from "dayjs";
import { isValid } from "date-fns";
import { type FC, useEffect, useMemo, useState } from "react";
import { weekDays } from "../../consts/consts";
import { useAlert } from "../../contexts/AlertProvider";
import { REQUEST_DEFAULT_VALUES } from "../../contexts/RequestProvider";
import { FormGoogle } from "../form-components/form-google-autocomplete/FormGoogle";

type DateEvent = (Dayjs & { $d?: Date }) | null;

type BulkGenerateMode = "by_days" | "by_locations";

type LocationRow = BulkLocationEntry & { id: string };

interface GroupOpportunitySlotsBulkGenerateProps {
  open: boolean;
  onClose: () => void;
  parentId: string;
  parentTitle?: string;
  defaultAmount?: number;
  existingChildren: GroupInstanceSlotIdentity[];
  onRefreshChildren?: () => Promise<GroupInstanceSlotIdentity[]>;
  onSuccess: () => void;
}

function newLocationRow(): LocationRow {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    location: "",
    coordinates: REQUEST_DEFAULT_VALUES.coordinates,
    isRemote: false,
  };
}

function defaultFormState(defaultAmount: number) {
  return {
    mode: "by_days" as BulkGenerateMode,
    selectedDays: [] as (typeof WEEK_DAYS)[number][],
    startFrom: dayjs().add(1, "day").startOf("day"),
    startTime: dayjs().hour(9).minute(0).second(0).millisecond(0),
    endTime: dayjs().hour(11).minute(0).second(0).millisecond(0),
    rangeMode: "weeks" as GroupInstanceScheduleRangeMode,
    weekCount: 4,
    endDate: dayjs().add(4, "week").startOf("day"),
    location: "",
    coordinates: REQUEST_DEFAULT_VALUES.coordinates,
    isRemote: false,
    amount: defaultAmount,
    locationRows: [newLocationRow()] as LocationRow[],
    multiHasTime: true,
    multiDate: dayjs().add(1, "day").hour(9).minute(0).second(0).millisecond(0),
    multiStartTime: dayjs().hour(9).minute(0).second(0).millisecond(0),
    multiEndTime: dayjs().hour(11).minute(0).second(0).millisecond(0),
    multiTimeDescription: "",
  };
}

function statusLabel(status: GroupInstanceSlotPreview["status"]): string {
  if (status === "duplicate") return ADMIN_GROUP_BULK_STATUS_SKIP;
  if (status === "past") return ADMIN_GROUP_BULK_STATUS_PAST;
  return ADMIN_GROUP_BULK_STATUS_NEW;
}

function formatSchedulePreview(row: {
  hasTime: boolean;
  startDate: Date | null;
  endDate: Date | null;
  timeDescription: string;
}): string {
  if (!row.hasTime) return row.timeDescription || "-";
  if (!row.startDate || !row.endDate) return "-";
  return `${dayjs(row.startDate).format("DD/MM/YYYY HH:mm")} - ${dayjs(row.endDate).format("HH:mm")}`;
}

export const GroupOpportunitySlotsBulkGenerate: FC<
  GroupOpportunitySlotsBulkGenerateProps
> = ({
  open,
  onClose,
  parentId,
  parentTitle,
  defaultAmount = 1,
  existingChildren,
  onRefreshChildren,
  onSuccess,
}) => {
  const { setAlertText, setSeverity } = useAlert();
  const [mode, setMode] = useState<BulkGenerateMode>("by_days");
  const [selectedDays, setSelectedDays] = useState<
    (typeof WEEK_DAYS)[number][]
  >(() => defaultFormState(defaultAmount).selectedDays);
  const [startFrom, setStartFrom] = useState<Dayjs>(
    () => defaultFormState(defaultAmount).startFrom,
  );
  const [startTime, setStartTime] = useState<Dayjs>(
    () => defaultFormState(defaultAmount).startTime,
  );
  const [endTime, setEndTime] = useState<Dayjs>(
    () => defaultFormState(defaultAmount).endTime,
  );
  const [rangeMode, setRangeMode] = useState<GroupInstanceScheduleRangeMode>(
    () => defaultFormState(defaultAmount).rangeMode,
  );
  const [weekCount, setWeekCount] = useState(
    () => defaultFormState(defaultAmount).weekCount,
  );
  const [endDate, setEndDate] = useState<Dayjs>(
    () => defaultFormState(defaultAmount).endDate,
  );
  const [location, setLocation] = useState(
    () => defaultFormState(defaultAmount).location,
  );
  const [coordinates, setCoordinates] = useState(
    () => defaultFormState(defaultAmount).coordinates,
  );
  const [isRemote, setIsRemote] = useState(
    () => defaultFormState(defaultAmount).isRemote,
  );
  const [amount, setAmount] = useState(
    () => defaultFormState(defaultAmount).amount,
  );
  const [locationRows, setLocationRows] = useState<LocationRow[]>(() =>
    defaultFormState(defaultAmount).locationRows,
  );
  const [multiHasTime, setMultiHasTime] = useState(true);
  const [multiDate, setMultiDate] = useState<Dayjs>(
    () => defaultFormState(defaultAmount).multiDate,
  );
  const [multiStartTime, setMultiStartTime] = useState<Dayjs>(
    () => defaultFormState(defaultAmount).multiStartTime,
  );
  const [multiEndTime, setMultiEndTime] = useState<Dayjs>(
    () => defaultFormState(defaultAmount).multiEndTime,
  );
  const [multiTimeDescription, setMultiTimeDescription] = useState("");
  const [daysPreview, setDaysPreview] = useState<
    GroupInstanceSlotPreview[] | null
  >(null);
  const [locationsPreview, setLocationsPreview] = useState<
    MultiLocationSlotPreview[] | null
  >(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    const defaults = defaultFormState(defaultAmount);
    setMode(defaults.mode);
    setSelectedDays(defaults.selectedDays);
    setStartFrom(defaults.startFrom);
    setStartTime(defaults.startTime);
    setEndTime(defaults.endTime);
    setRangeMode(defaults.rangeMode);
    setWeekCount(defaults.weekCount);
    setEndDate(defaults.endDate);
    setLocation(defaults.location);
    setCoordinates(defaults.coordinates);
    setIsRemote(defaults.isRemote);
    setAmount(defaults.amount);
    setLocationRows(defaults.locationRows);
    setMultiHasTime(defaults.multiHasTime);
    setMultiDate(defaults.multiDate);
    setMultiStartTime(defaults.multiStartTime);
    setMultiEndTime(defaults.multiEndTime);
    setMultiTimeDescription(defaults.multiTimeDescription);
    setDaysPreview(null);
    setLocationsPreview(null);
  }, [open, defaultAmount]);

  const scheduleConfig = useMemo(
    () => ({
      days: selectedDays,
      startFrom: startFrom.toDate(),
      startTime: {
        hour: startTime.hour(),
        minute: startTime.minute(),
      },
      endTime: {
        hour: endTime.hour(),
        minute: endTime.minute(),
      },
      rangeMode,
      endDate: rangeMode === "until_date" ? endDate.toDate() : undefined,
      weekCount: rangeMode === "weeks" ? weekCount : undefined,
    }),
    [
      selectedDays,
      startFrom,
      startTime,
      endTime,
      rangeMode,
      endDate,
      weekCount,
    ],
  );

  const multiLocationSchedule = useMemo((): BulkMultiLocationSchedule => {
    if (!multiHasTime) {
      return {
        hasTime: false,
        timeDescription: multiTimeDescription,
      };
    }
    const start = multiDate
      .hour(multiStartTime.hour())
      .minute(multiStartTime.minute())
      .second(0)
      .millisecond(0);
    const end = multiDate
      .hour(multiEndTime.hour())
      .minute(multiEndTime.minute())
      .second(0)
      .millisecond(0);
    return {
      hasTime: true,
      startDate: start.toDate(),
      endDate: end.toDate(),
    };
  }, [
    multiHasTime,
    multiDate,
    multiStartTime,
    multiEndTime,
    multiTimeDescription,
  ]);

  const activePreview = mode === "by_days" ? daysPreview : locationsPreview;
  const previewCounts = useMemo(
    () => (activePreview ? countSchedulePreview(activePreview) : null),
    [activePreview],
  );

  const clearPreview = () => {
    setDaysPreview(null);
    setLocationsPreview(null);
  };

  const validateDaysForm = (): string | null => {
    const scheduleError = validateGroupInstanceScheduleConfig(scheduleConfig);
    if (scheduleError) return scheduleError;
    if (!isRemote && !location.trim()) {
      return "יש למלא מיקום או לסמן התנדבות מרחוק";
    }
    if (!isRemote && !hasValidCoordinates(coordinates)) {
      return "יש לבחור מיקום מהרשימה או מהמפה";
    }
    if (amount < 1) return "יש להזין לפחות מקום אחד";
    return null;
  };

  const validateLocationsForm = (): string | null => {
    const scheduleError = validateBulkMultiLocationConfig(
      locationRows,
      multiLocationSchedule,
    );
    if (scheduleError) return scheduleError;
    if (amount < 1) return "יש להזין לפחות מקום אחד";
    return null;
  };

  const handlePreview = () => {
    if (mode === "by_days") {
      const validationError = validateDaysForm();
      if (validationError) {
        setSeverity("warning");
        setAlertText(validationError);
        return;
      }
      const annotated = buildBulkGroupChildPreview(
        scheduleConfig,
        existingChildren,
        { location, isRemote },
      );
      setDaysPreview(annotated);
      setLocationsPreview(null);
      const counts = countSchedulePreview(annotated);
      if (counts.toCreate === 0) {
        setSeverity("warning");
        setAlertText("אין מופעים חדשים ליצירה בטווח שנבחר");
      }
      return;
    }

    const validationError = validateLocationsForm();
    if (validationError) {
      setSeverity("warning");
      setAlertText(validationError);
      return;
    }
    const annotated = buildBulkMultiLocationPreview(
      locationRows,
      multiLocationSchedule,
      existingChildren,
    );
    setLocationsPreview(annotated);
    setDaysPreview(null);
    const counts = countSchedulePreview(annotated);
    if (counts.toCreate === 0) {
      setSeverity("warning");
      setAlertText("אין מופעים חדשים ליצירה");
    }
  };

  const handleCreate = async () => {
    if (!activePreview || !previewCounts || previewCounts.toCreate === 0) {
      setSeverity("warning");
      setAlertText("יש ללחוץ על תצוגה מקדימה לפני יצירת המופעים");
      return;
    }

    const validationError =
      mode === "by_days" ? validateDaysForm() : validateLocationsForm();
    if (validationError) {
      setSeverity("warning");
      setAlertText(validationError);
      return;
    }

    const latestChildren = onRefreshChildren
      ? await onRefreshChildren()
      : existingChildren;

    let slots;
    if (mode === "by_days") {
      const freshPreview = buildBulkGroupChildPreview(
        scheduleConfig,
        latestChildren,
        { location, isRemote },
      );
      const counts = countSchedulePreview(freshPreview);
      setDaysPreview(freshPreview);
      if (counts.toCreate === 0) {
        setSeverity("warning");
        setAlertText("אין מופעים חדשים ליצירה בטווח שנבחר");
        return;
      }
      if (counts.toCreate > GROUP_INSTANCE_BULK_MAX) {
        setSeverity("warning");
        setAlertText(
          `ניתן ליצור עד ${GROUP_INSTANCE_BULK_MAX} מופעים בפעולה אחת`,
        );
        return;
      }
      slots = buildBulkGroupChildSlots(freshPreview, {
        amount,
        location,
        coordinates,
        isRemote,
      });
    } else {
      const freshPreview = buildBulkMultiLocationPreview(
        locationRows,
        multiLocationSchedule,
        latestChildren,
      );
      const counts = countSchedulePreview(freshPreview);
      setLocationsPreview(freshPreview);
      if (counts.toCreate === 0) {
        setSeverity("warning");
        setAlertText("אין מופעים חדשים ליצירה");
        return;
      }
      if (counts.toCreate > GROUP_INSTANCE_BULK_MAX) {
        setSeverity("warning");
        setAlertText(
          `ניתן ליצור עד ${GROUP_INSTANCE_BULK_MAX} מופעים בפעולה אחת`,
        );
        return;
      }
      slots = buildBulkMultiLocationSlots(freshPreview, amount);
    }

    setIsCreating(true);
    try {
      const res = await Opportunity.bulkGenerateGroupChildSlots(parentId, {
        slots,
        skipDuplicates: true,
      });

      const hasPartialFailure = res.failed.length > 0;
      const hasSuccess = res.created > 0;
      setSeverity(
        hasSuccess && !hasPartialFailure
          ? "success"
          : hasSuccess
            ? "warning"
            : "error",
      );
      setAlertText(res.message);

      if (hasSuccess) {
        onSuccess();
        onClose();
      }
    } catch {
      setSeverity("error");
      setAlertText(ADMIN_GROUP_BULK_ERROR);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (isCreating) return;
    clearPreview();
    onClose();
  };

  const updateLocationRow = (
    id: string,
    patch: Partial<Omit<LocationRow, "id">>,
  ) => {
    clearPreview();
    setLocationRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography variant="h6" color="primary">
          {ADMIN_GROUP_BULK_GENERATE_TITLE}
        </Typography>
        {parentTitle && (
          <Typography variant="body2" color="text.secondary">
            {parentTitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <FormControl>
            <RadioGroup
              row
              value={mode}
              onChange={(e) => {
                clearPreview();
                setMode(e.target.value as BulkGenerateMode);
              }}
            >
              <FormControlLabel
                value="by_days"
                control={<Radio size="small" />}
                label={ADMIN_GROUP_BULK_MODE_BY_DAYS}
              />
              <FormControlLabel
                value="by_locations"
                control={<Radio size="small" />}
                label={ADMIN_GROUP_BULK_MODE_BY_LOCATIONS}
              />
            </RadioGroup>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            {mode === "by_days"
              ? ADMIN_GROUP_BULK_GENERATE_HINT
              : ADMIN_GROUP_BULK_LOCATIONS_HINT}
          </Typography>

          {mode === "by_days" ? (
            <>
              <Box>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                  {ADMIN_GROUP_BULK_DAYS_LABEL}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {weekDays.map((day) => {
                    const value = day.value as (typeof WEEK_DAYS)[number];
                    const chosen = selectedDays.includes(value);
                    return (
                      <Button
                        key={day.value}
                        size="small"
                        variant={chosen ? "contained" : "outlined"}
                        onClick={() => {
                          clearPreview();
                          setSelectedDays((prev) =>
                            prev.includes(value)
                              ? prev.filter((d) => d !== value)
                              : [...prev, value],
                          );
                        }}
                        sx={{ minWidth: 40 }}
                      >
                        {day.name}
                      </Button>
                    );
                  })}
                </Stack>
              </Box>

              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                localeText={
                  heIL.components.MuiLocalizationProvider.defaultProps
                    .localeText
                }
                adapterLocale="he"
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  gap={2}
                  alignItems={{ sm: "center" }}
                >
                  <DatePicker
                    label={ADMIN_GROUP_BULK_START_DATE}
                    format="DD/MM/YYYY"
                    disablePast
                    value={startFrom}
                    onChange={(event: DateEvent) => {
                      if (!event || !isValid(event.toDate())) return;
                      clearPreview();
                      setStartFrom(dayjs(event.$d ?? event.toDate()));
                    }}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                  <TimePicker
                    label="משעה"
                    format="HH:mm"
                    timeSteps={{ minutes: 15 }}
                    value={startTime}
                    onChange={(event: DateEvent) => {
                      if (!event || !isValid(event.toDate())) return;
                      clearPreview();
                      setStartTime(dayjs(event.$d ?? event.toDate()));
                    }}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                  <TimePicker
                    label="עד שעה"
                    format="HH:mm"
                    timeSteps={{ minutes: 15 }}
                    minTime={startTime}
                    value={endTime}
                    onChange={(event: DateEvent) => {
                      if (!event || !isValid(event.toDate())) return;
                      clearPreview();
                      setEndTime(dayjs(event.$d ?? event.toDate()));
                    }}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </Stack>

                <FormControl sx={{ mt: 1 }}>
                  <RadioGroup
                    row
                    value={rangeMode}
                    onChange={(e) => {
                      clearPreview();
                      setRangeMode(
                        e.target.value as GroupInstanceScheduleRangeMode,
                      );
                    }}
                  >
                    <FormControlLabel
                      value="weeks"
                      control={<Radio size="small" />}
                      label={ADMIN_GROUP_BULK_RANGE_WEEKS}
                    />
                    <FormControlLabel
                      value="until_date"
                      control={<Radio size="small" />}
                      label={ADMIN_GROUP_BULK_RANGE_UNTIL_DATE}
                    />
                  </RadioGroup>
                </FormControl>

                {rangeMode === "weeks" ? (
                  <TextField
                    label={ADMIN_GROUP_BULK_WEEK_COUNT}
                    type="number"
                    size="small"
                    inputProps={{ min: 1, max: 52 }}
                    value={weekCount}
                    onChange={(e) => {
                      clearPreview();
                      setWeekCount(
                        Math.min(52, Math.max(1, Number(e.target.value) || 1)),
                      );
                    }}
                    sx={{ maxWidth: 200 }}
                  />
                ) : (
                  <DatePicker
                    label={ADMIN_GROUP_BULK_END_DATE}
                    format="DD/MM/YYYY"
                    disablePast
                    minDate={startFrom}
                    value={endDate}
                    onChange={(event: DateEvent) => {
                      if (!event || !isValid(event.toDate())) return;
                      clearPreview();
                      setEndDate(dayjs(event.$d ?? event.toDate()));
                    }}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                )}
              </LocalizationProvider>

              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isRemote}
                    onChange={(e) => {
                      clearPreview();
                      setIsRemote(e.target.checked);
                    }}
                  />
                }
                label="התנדבות מרחוק"
              />

              {!isRemote && (
                <FormGoogle
                  label="מיקום"
                  fullWidth
                  size="small"
                  placeValue={location}
                  onLocationChange={({ locationName, latLng }) => {
                    clearPreview();
                    setLocation(locationName);
                    setCoordinates(latLng);
                  }}
                />
              )}
            </>
          ) : (
            <>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                localeText={
                  heIL.components.MuiLocalizationProvider.defaultProps
                    .localeText
                }
                adapterLocale="he"
              >
                <FormControlLabel
                  sx={{ m: 0, alignSelf: "flex-start" }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!multiHasTime}
                      onChange={(e) => {
                        clearPreview();
                        setMultiHasTime(!e.target.checked);
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" fontSize="0.82rem">
                      ללא זמן מוגדר
                    </Typography>
                  }
                />

                {multiHasTime ? (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    gap={2}
                    alignItems={{ sm: "center" }}
                  >
                    <DatePicker
                      label="בתאריך"
                      format="DD/MM/YYYY"
                      disablePast
                      value={multiDate}
                      onChange={(event: DateEvent) => {
                        if (!event || !isValid(event.toDate())) return;
                        clearPreview();
                        setMultiDate(dayjs(event.$d ?? event.toDate()));
                      }}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                    <TimePicker
                      label="משעה"
                      format="HH:mm"
                      timeSteps={{ minutes: 15 }}
                      value={multiStartTime}
                      onChange={(event: DateEvent) => {
                        if (!event || !isValid(event.toDate())) return;
                        clearPreview();
                        setMultiStartTime(dayjs(event.$d ?? event.toDate()));
                      }}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                    <TimePicker
                      label="עד שעה"
                      format="HH:mm"
                      timeSteps={{ minutes: 15 }}
                      minTime={multiStartTime}
                      value={multiEndTime}
                      onChange={(event: DateEvent) => {
                        if (!event || !isValid(event.toDate())) return;
                        clearPreview();
                        setMultiEndTime(dayjs(event.$d ?? event.toDate()));
                      }}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </Stack>
                ) : (
                  <TextField
                    label="תיאור זמן"
                    placeholder="למשל: פעם בשבוע, כל יום..."
                    size="small"
                    fullWidth
                    value={multiTimeDescription}
                    onChange={(e) => {
                      clearPreview();
                      setMultiTimeDescription(
                        e.target.value.slice(0, TIME_DESCRIPTION_MAX_LENGTH),
                      );
                    }}
                    inputProps={{ maxLength: TIME_DESCRIPTION_MAX_LENGTH }}
                  />
                )}
              </LocalizationProvider>

              <Stack gap={1.5}>
                {locationRows.map((row, index) => (
                  <Stack
                    key={row.id}
                    direction={{ xs: "column", sm: "row" }}
                    gap={1}
                    alignItems={{ sm: "center" }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, flexShrink: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={row.isRemote}
                          onChange={(e) =>
                            updateLocationRow(row.id, {
                              isRemote: e.target.checked,
                            })
                          }
                        />
                      }
                      label={
                        <Typography variant="body2" fontSize="0.82rem">
                          מרחוק
                        </Typography>
                      }
                    />
                    {!row.isRemote ? (
                      <FormGoogle
                        label={ADMIN_GROUP_BULK_LOCATION_ROW(index + 1)}
                        fullWidth
                        size="small"
                        placeValue={row.location}
                        onLocationChange={({ locationName, latLng }) =>
                          updateLocationRow(row.id, {
                            location: locationName,
                            coordinates: latLng,
                          })
                        }
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ flex: 1 }}
                      >
                        {ADMIN_GROUP_BULK_LOCATION_ROW(index + 1)} - מרחוק
                      </Typography>
                    )}
                    <IconButton
                      size="small"
                      disabled={locationRows.length <= 1}
                      onClick={() => {
                        clearPreview();
                        setLocationRows((prev) =>
                          prev.filter((r) => r.id !== row.id),
                        );
                      }}
                      aria-label="הסר מיקום"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    clearPreview();
                    setLocationRows((prev) => [...prev, newLocationRow()]);
                  }}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {ADMIN_GROUP_BULK_ADD_LOCATION}
                </Button>
              </Stack>
            </>
          )}

          <TextField
            label="מספר מתנדבים נדרש"
            type="number"
            size="small"
            inputProps={{ min: 1 }}
            value={amount}
            onChange={(e) => {
              clearPreview();
              setAmount(Math.max(1, Number(e.target.value) || 1));
            }}
            sx={{ maxWidth: 200 }}
          />

          {daysPreview && previewCounts && mode === "by_days" && (
            <Stack gap={1}>
              <Typography variant="subtitle2">
                {ADMIN_GROUP_BULK_PREVIEW_SUMMARY(
                  previewCounts.toCreate,
                  previewCounts.toSkip,
                )}
              </Typography>
              {previewCounts.toCreate >= GROUP_INSTANCE_BULK_WARN_THRESHOLD && (
                <Alert severity="warning">
                  {ADMIN_GROUP_BULK_WARN_MANY(previewCounts.toCreate)}
                </Alert>
              )}
              <Box sx={{ maxHeight: 280, overflow: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>תאריך</TableCell>
                      <TableCell>שעות</TableCell>
                      <TableCell>מיקום</TableCell>
                      <TableCell>סטטוס</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {daysPreview.map((row) => (
                      <TableRow key={row.startDate.toISOString()}>
                        <TableCell>
                          {dayjs(row.startDate).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell>
                          {`${dayjs(row.startDate).format("HH:mm")} - ${dayjs(row.endDate).format("HH:mm")}`}
                        </TableCell>
                        <TableCell>
                          {isRemote ? "מרחוק" : location || "-"}
                        </TableCell>
                        <TableCell>{statusLabel(row.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          )}

          {locationsPreview && previewCounts && mode === "by_locations" && (
            <Stack gap={1}>
              <Typography variant="subtitle2">
                {ADMIN_GROUP_BULK_PREVIEW_SUMMARY(
                  previewCounts.toCreate,
                  previewCounts.toSkip,
                )}
              </Typography>
              {previewCounts.toCreate >= GROUP_INSTANCE_BULK_WARN_THRESHOLD && (
                <Alert severity="warning">
                  {ADMIN_GROUP_BULK_WARN_MANY(previewCounts.toCreate)}
                </Alert>
              )}
              <Box sx={{ maxHeight: 280, overflow: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>מיקום</TableCell>
                      <TableCell>זמן</TableCell>
                      <TableCell>סטטוס</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locationsPreview.map((row, index) => (
                      <TableRow
                        key={`${row.location}-${row.status}-${index}`}
                      >
                        <TableCell>
                          {row.isRemote ? "מרחוק" : row.location}
                        </TableCell>
                        <TableCell>{formatSchedulePreview(row)}</TableCell>
                        <TableCell>{statusLabel(row.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isCreating}>
          ביטול
        </Button>
        <Button
          variant="outlined"
          onClick={handlePreview}
          disabled={isCreating}
        >
          {ADMIN_GROUP_BULK_PREVIEW}
        </Button>
        <Button
          variant="contained"
          disabled={
            isCreating ||
            !activePreview ||
            !previewCounts ||
            previewCounts.toCreate === 0 ||
            previewCounts.toCreate > GROUP_INSTANCE_BULK_MAX
          }
          onClick={() => void handleCreate()}
        >
          {isCreating ? (
            <CircularProgress size={22} />
          ) : previewCounts ? (
            ADMIN_GROUP_BULK_CREATE(previewCounts.toCreate)
          ) : (
            ADMIN_GROUP_BULK_PREVIEW
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
